import { useFormContext } from "react-hook-form";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import {
  selectBasicQuestions,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { FormRadioGroup, FormTextField } from "@covertree/ui";

import { HomeDetailsForm } from "../validatorsAndTypes/homeDetailsAndConstructionForm";

interface Props {
  label?: React.ReactNode;
  placeholder?: string;
  tooltip?: string;
  disabled?: boolean;
  fontSize?: number;
  labelFontSize?: number;
  shouldReValidateOnModeChange?: boolean;
}

const InputMode = {
  SquareFootage: "Enter square footage",
  LengthWidth: "Enter Length/Width",
} as const;

interface SavedValues {
  length?: number;
  width?: number;
  squareFootage?: number;
}

const calculateSquareFootage = (
  length?: number | null,
  width?: number | null
) =>
  Number(length) > 0 && Number(width) > 0
    ? Number(length) * Number(width)
    : undefined;

/**
 * HomeSizeInput Value Preservation & Clearing Logic:
 * - When switching modes, preserve previous values (calculated or manual) to allow restoration
 * - Saved length/width are restored when switching back to dimensions mode
 * - If user manually edits square footage to a different value, clear saved dimensions
 */
export function HomeSizeInput({
  placeholder = "Enter square footage",
  tooltip = "Enter the total square footage of the home (including additions). You can either enter it directly or calculate it from length and width.",
  disabled = false,
  fontSize,
  labelFontSize,
  shouldReValidateOnModeChange = true,
}: Props) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const { totalSquareFootage } = useNewBusinessSelector(selectBasicQuestions);

  const {
    control,
    watch,
    setValue,
    trigger,
    resetField,
    clearErrors,
    formState: { touchedFields },
  } = useFormContext<HomeDetailsForm>();

  const homeLength = watch("homeLength");
  const homeWidth = watch("homeWidth");
  const totalSquareFootageValue = watch("totalSquareFootage");

  const [inputMode, setInputMode] = useState<string>(
    homeLength && homeWidth ? InputMode.LengthWidth : InputMode.SquareFootage
  );

  const saved = useRef<SavedValues>({}).current;

  const calculatedSquareFootage = calculateSquareFootage(homeLength, homeWidth);

  const handleInputModeChange = (newMode: string) => {
    if (newMode === inputMode) return;

    if (newMode === InputMode.SquareFootage) {
      saved.length = homeLength ? Number(homeLength) : undefined;
      saved.width = homeWidth ? Number(homeWidth) : undefined;
      setValue("homeLength", undefined, { shouldValidate: true });
      setValue("homeWidth", undefined, { shouldValidate: true });

      const squareFootage = calculatedSquareFootage || saved.squareFootage;

      if (squareFootage || touchedFields.totalSquareFootage) {
        setValue("totalSquareFootage", squareFootage ?? null, {
          shouldValidate: true,
        });
      } else {
        resetField("totalSquareFootage", { keepTouched: true });
      }
    } else {
      saved.squareFootage = totalSquareFootageValue
        ? Number(totalSquareFootageValue)
        : undefined;

      const squareFootage = calculateSquareFootage(saved.length, saved.width);

      clearErrors("totalSquareFootage");

      setValue("totalSquareFootage", squareFootage, {
        shouldValidate: shouldReValidateOnModeChange || !!squareFootage,
      });
      setValue("homeLength", saved.length ?? null, {
        shouldValidate: touchedFields.homeLength,
      });
      setValue("homeWidth", saved.width ?? null, {
        shouldValidate: touchedFields.homeWidth,
      });
    }

    setInputMode(newMode);
  };

  useEffect(() => {
    if (inputMode === InputMode.LengthWidth) {
      setValue("totalSquareFootage", calculatedSquareFootage);
      if (calculatedSquareFootage) trigger("totalSquareFootage");
    }
  }, [calculatedSquareFootage, setValue, trigger]);

  useEffect(() => {
    const savedSquareFootage = calculateSquareFootage(
      saved.length,
      saved.width
    );

    if (
      inputMode === InputMode.SquareFootage &&
      totalSquareFootageValue &&
      savedSquareFootage &&
      Number(totalSquareFootageValue) !== savedSquareFootage
    ) {
      saved.length = undefined;
      saved.width = undefined;
    }
  }, [totalSquareFootageValue, inputMode, saved]);

  return (
    <>
      <FormRadioGroup
        items={Object.values(InputMode).map((value) => ({
          title: value,
          value,
        }))}
        label="Home Size"
        tooltip="Enter the total square footage of the home (including additions). You can either enter it directly or calculate it from length and width."
        selected={inputMode}
        control={control}
        name="inputMode"
        onOptionSelect={(value) => handleInputModeChange(value as string)}
        direction={isDesktop ? "row" : "column"}
        variant="blank"
        disabled={disabled}
        fontSize={fontSize}
        labelFontSize={labelFontSize}
      />

      {inputMode === InputMode.SquareFootage ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} mt={3}>
            <FormTextField
              id="square-footage"
              dataTestId="square-footage-input"
              type="text"
              inputMode="numeric"
              control={control}
              name="totalSquareFootage"
              tooltip={tooltip}
              placeholder={placeholder}
              label={totalSquareFootage?.label}
              hiddenLabel
              disabled={disabled}
              fontSize={fontSize}
              labelFontSize={labelFontSize}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid
            container
            spacing={2}
            sx={{
              mt: { xs: 3, md: 0 },
            }}>
            <Grid item xs={12} md={6}>
              <FormTextField
                id="homeLength"
                dataTestId="homeLength-input"
                type="text"
                inputMode="numeric"
                control={control}
                name="homeLength"
                tooltip="Enter the length of the home in feet. Must be greater than 0."
                placeholder="Length"
                label="Length (ft)"
                disabled={disabled}
                fontSize={fontSize}
                labelFontSize={labelFontSize}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                id="homeWidth"
                dataTestId="homeWidth-input"
                type="text"
                inputMode="numeric"
                control={control}
                name="homeWidth"
                tooltip="Enter the width of the home in feet. Must be greater than 0."
                placeholder="Width"
                label="Width (ft)"
                disabled={disabled}
                fontSize={fontSize}
                labelFontSize={labelFontSize}
              />
            </Grid>

            {calculatedSquareFootage && (
              <Grid item xs={12} md={6}>
                <FormTextField
                  id="square-footage"
                  dataTestId="square-footage-input"
                  type="text"
                  inputMode="numeric"
                  control={control}
                  name="totalSquareFootage"
                  label="Calculated Square Footage"
                  value={calculatedSquareFootage?.toString() || ""}
                  disabled
                  readOnly
                  fontSize={fontSize}
                  labelFontSize={labelFontSize}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
}
